import React, { Component } from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Checklist } from '../../components/Checklist';
import { DATA_BACKUP_CHECKLIST } from '../../utils/constants';
import { Database, Cloud, HardDrive, Lock, AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
export function DataBackup() {
  return <Layout data-id="element-4548">
      <div className="container mx-auto px-4 py-12" data-id="element-4549">
        {/* Hero */}
        <div className="mb-12" data-id="element-4550">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-500 text-xs font-medium mb-4 border border-blue-500/20" data-id="element-4551">
            <Database className="h-3 w-3 mr-2" data-id="element-4552" />
            Personal Protocol
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4553">
            Data Protection & Backup
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-4554">
            Protect your data from loss, corruption, and ransomware. A robust
            backup strategy is your only insurance against digital disaster.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8" data-id="element-4555">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-4556">
            {/* Why This Matters */}
            <section data-id="element-4557">
              <h2 className="text-2xl font-bold text-slate-100 mb-4" data-id="element-4558">
                Why This Matters
              </h2>
              <div className="bg-slate-900/50 border-l-4 border-blue-500 p-6 rounded-r-lg" data-id="element-4559">
                <p className="text-slate-300 mb-4" data-id="element-4560">
                  Data loss is not a matter of "if", but "when". Hard drives
                  fail, ransomware encrypts files, and accidental deletions
                  happen. Without a backup, your digital life is gone forever.
                </p>
                <ul className="space-y-2 text-slate-400" data-id="element-4561">
                  <li className="flex items-start" data-id="element-4562">
                    <span className="text-blue-500 mr-2" data-id="element-4563">•</span> Ransomware is
                    a growing threat targeting individuals
                  </li>
                  <li className="flex items-start" data-id="element-4564">
                    <span className="text-blue-500 mr-2" data-id="element-4565">•</span> Cloud services
                    can lose data or lock your account
                  </li>
                  <li className="flex items-start" data-id="element-4566">
                    <span className="text-blue-500 mr-2" data-id="element-4567">•</span> Hardware
                    failure is inevitable (all drives die eventually)
                  </li>
                </ul>
              </div>
            </section>

            {/* Core Components */}
            <section data-id="element-4568">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4569">
                Core Components
              </h2>
              <div className="grid gap-6" data-id="element-4570">
                <ComponentCard title="3-2-1 Backup Rule" desc="3 copies of data, 2 different media types, 1 copy offsite. The gold standard of backup." icon={Database} data-id="element-4571" />
                <ComponentCard title="Backup Types" desc="Combine local backups (fast restore) with cloud backups (offsite protection) and offline backups (ransomware proof)." icon={Cloud} data-id="element-4572" />
                <ComponentCard title="Encryption" desc="Encrypt backups at rest, especially in the cloud. Use strong encryption (AES-256) and secure your keys." icon={Lock} data-id="element-4573" />
                <ComponentCard title="Testing" desc="A backup is useless if you can't restore it. Test your restore procedures regularly." icon={HardDrive} data-id="element-4574" />
              </div>
            </section>

            {/* Implementation Steps */}
            <section data-id="element-4575">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4576">
                Implementation Steps
              </h2>
              <div className="space-y-4" data-id="element-4577">
                <Step number={1} text="Choose backup destinations (local drive + cloud service)." data-id="element-4578" />
                <Step number={2} text="Set up automatic backups (daily or weekly schedule)." data-id="element-4579" />
                <Step number={3} text="Encrypt all cloud backups before upload." data-id="element-4580" />
                <Step number={4} text="Create an offline backup (disconnected drive) for ransomware protection." data-id="element-4581" />
                <Step number={5} text="Test restore from each backup location to verify integrity." data-id="element-4582" />
              </div>
            </section>

            {/* Common Mistakes */}
            <section data-id="element-4583">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4584">
                Common Mistakes
              </h2>
              <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-lg" data-id="element-4585">
                <ul className="space-y-3 text-slate-300" data-id="element-4586">
                  <li className="flex items-start" data-id="element-4587">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4588" />
                    Only backing up to one location (single point of failure).
                  </li>
                  <li className="flex items-start" data-id="element-4589">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4590" />
                    Storing backup drive permanently connected to computer
                    (vulnerable to ransomware).
                  </li>
                  <li className="flex items-start" data-id="element-4591">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" data-id="element-4592" />
                    Not testing restores—you don't have a backup until you've
                    successfully restored it.
                  </li>
                </ul>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4593">
            <Card className="p-6 sticky top-24" data-id="element-4594">
              <h3 className="text-lg font-bold text-slate-100 mb-4" data-id="element-4595">
                Backup Checklist
              </h3>
              <Checklist items={DATA_BACKUP_CHECKLIST} id="personal-backup" data-id="element-4596" />

              <div className="mt-8 pt-6 border-t border-slate-800" data-id="element-4597">
                <h4 className="text-sm font-bold text-slate-400 uppercase mb-4" data-id="element-4598">
                  Next Steps
                </h4>
                <Link to="/devices/windows" className="block w-full" data-id="element-4599">
                  <Button className="w-full bg-blue-500 hover:bg-blue-600 text-slate-950" data-id="element-4600">
                    Next: Windows Security{' '}
                    <ArrowRight className="ml-2 h-4 w-4" data-id="element-4601" />
                  </Button>
                </Link>
                <Link to="/tools/backup" className="block w-full mt-3" data-id="element-4602">
                  <Button variant="secondary" className="w-full" data-id="element-4603">
                    Backup Tools
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}
function ComponentCard({
  title,
  desc,
  icon: Icon
}: {
  title: string;
  desc: string;
  icon: any;
}) {
  return <div className="bg-slate-900 p-6 rounded-lg border border-slate-800 flex items-start" data-id="element-4604">
      <div className="bg-blue-500/10 p-3 rounded-lg mr-4 flex-shrink-0" data-id="element-4605">
        <Icon className="h-6 w-6 text-blue-500" data-id="element-4606" />
      </div>
      <div data-id="element-4607">
        <h3 className="text-lg font-bold text-slate-200 mb-2" data-id="element-4608">{title}</h3>
        <p className="text-slate-400 text-sm" data-id="element-4609">{desc}</p>
      </div>
    </div>;
}
function Step({
  number,
  text
}: {
  number: number;
  text: string;
}) {
  return <div className="flex items-center bg-slate-900/50 p-4 rounded border border-slate-800" data-id="element-4610">
      <div className="w-8 h-8 rounded-full bg-slate-800 text-blue-500 font-bold flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-4611">
        {number}
      </div>
      <p className="text-slate-300" data-id="element-4612">{text}</p>
    </div>;
}